import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CircleCheck, CircleDashed, Info, OctagonAlert, PlugZap, RefreshCw, TriangleAlert } from 'lucide-react';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Button } from '@/deploy/components/ui/Button';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { getGameById } from '@/config/games';
import * as api from './profiler.api';
import { SPIKE_THRESHOLD_MS, TARGET_TPS, THRESHOLDS, TICK_BUDGET_MS, budgetText, headlineLevel } from './profiler.health';
import { allRows, countLights, listOutcome, serverPath, splitRows, type GroupResult, type KnownServer, type Row } from './profiler.servers';
import { fmtAgo, fmtBytes, fmtClock, fmtMs, fmtNumber, fmtTps } from './profiler.format';
import { BudgetMeter, Note, StatTile, StatusLight, cx } from './ProfilerParts';
import { useProfilerGroups, type ProfilerGroup } from './useProfilerGroups';
import s from './Profiler.module.scss';

// ============================================================
// Déploiement > Profileur : la santé des serveurs de jeu, vue d'ensemble
// ============================================================
// Le moteur des serveurs (Endium 0.1.48) mesure chaque tick ; un mod compagnon envoie ces mesures au
// satellite du projet ; cette page les lit par la passerelle du panel, comme la page Crashs lit les
// rapports de Watchdog. Elle ne dépend donc pas de la connexion au CICD de l'espace Déploiement.
//
// Pour quelqu'un qui n'est pas développeur : un voyant par serveur, la phrase qui l'explique, et les
// chiffres face au budget (un tick a 50 ms pour finir). Un serveur dont le profileur est éteint, ou
// qui n'envoie rien, est gris : on ne sait rien de sa santé, il n'est pas « en panne ».
// ============================================================

/** Rafraîchissement des mesures : le mod envoie un instantané par minute. */
const POLL_MS = 30_000;
/**
 * Espace insécable. Sur un écran de portable les titres des colonnes du tableau se plient sur deux
 * lignes (Profiler.module.scss) : elle dit où ils ne doivent pas se couper, pour donner « Ticks par /
 * seconde » et « Mémoire / par tick », et jamais trois lignes.
 */
const NBSP = '\u00a0';

async function loadGroup(group: ProfilerGroup, known: KnownServer[] | null): Promise<GroupResult> {
  const res = await api.listServers(group.key);
  return { key: group.key, label: group.label, ...listOutcome(res), known };
}

export function ProfilerPage() {
  const navigate = useNavigate();
  const { gameId, groups, ready } = useProfilerGroups();
  const gameName = getGameById(gameId).name;
  const [results, setResults] = useState<GroupResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  // Serveurs de la Console, par groupe : lus au chargement et sur « Rafraîchir », pas à chaque relève
  // (la Console interroge Pterodactyl serveur par serveur).
  const known = useRef<Map<string, KnownServer[] | null>>(new Map());
  const run = useRef(0);

  const load = useCallback(async (withConsole: boolean) => {
    const mine = ++run.current;
    setLoading(true);
    if (withConsole) {
      const lists = await Promise.all(groups.map((g) => api.listKnownServers(g.key)));
      if (mine !== run.current) return;
      known.current = new Map(groups.map((g, i) => [g.key ?? '', lists[i]]));
    }
    const next = await Promise.all(groups.map((g) => loadGroup(g, known.current.get(g.key ?? '') ?? null)));
    if (mine !== run.current) return;
    setResults(next);
    setLoadedAt(Date.now());
    setLoading(false);
  }, [groups]);

  // Changement de projet ou de groupes : on repart de zéro (jamais les serveurs d'un autre projet à l'écran).
  useEffect(() => {
    if (!ready) return;
    setResults(null);
    known.current = new Map();
    void load(true);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(false); }, POLL_MS);
    return () => { run.current++; window.clearInterval(timer); };
  }, [ready, load]);

  const rows = useMemo(() => (results ? allRows(results) : []), [results]);
  // Le tableau : les serveurs qui ont déjà envoyé une mesure. Les autres serveurs de la Console sont
  // regroupés sous le tableau, repliés.
  const { measured, neverSeen } = useMemo(() => splitRows(rows), [rows]);
  const lights = useMemo(() => countLights(measured), [measured]);
  const multiGroup = groups.length > 1;
  const groupOf = (key: string | null) => groups.find((g) => g.key === key) ?? null;
  const offGroups = (results ?? []).filter((r) => r.moduleOff);
  const unavailableGroups = (results ?? []).filter((r) => r.unavailable);
  const failedGroups = (results ?? []).filter((r) => r.error);
  const answered = (results ?? []).filter((r) => r.list);
  const consoleMissing = answered.some((r) => r.known == null);

  return (
    <div className={s.page}>
      <PageHeader
        eyebrow="Diagnostic"
        title="Profileur"
        subtitle={`La santé des serveurs de jeu de ${gameName}, mesurée par le moteur à chaque pas du jeu.`}
        actions={(
          <>
            {loadedAt && <span className={s.updated}>Mis à jour à {fmtClock(loadedAt)}</span>}
            <Button size="sm" leftIcon={<RefreshCw size={14} />} loading={loading && results != null} onClick={() => void load(true)}>
              Rafraîchir
            </Button>
          </>
        )}
      />

      <details className={s.guide}>
        <summary><Info size={15} aria-hidden /> Comment lire cette page</summary>
        <div className={s.guideBody}>
          <p>
            Un serveur de jeu avance par petits pas, les <strong>ticks</strong> : {TARGET_TPS} par seconde. Chaque tick a donc
            {' '}<strong>{TICK_BUDGET_MS} millisecondes</strong> pour tout faire (déplacer les joueurs et les créatures, faire évoluer la carte).
            S'il met plus longtemps, le jeu ralentit ; au-delà de {SPIKE_THRESHOLD_MS} ms, les joueurs sentent un à-coup : c'est un <strong>pic</strong>.
          </p>
          <ul className={s.guideList}>
            <li><StatusLight health={{ state: 'measuring', light: 'ok', title: 'Vert', reason: '', level: null }} /> le monde tient son rythme, avec de la marge.</li>
            <li>
              <StatusLight health={{ state: 'measuring', light: 'watch', title: 'Orange', reason: '', level: null }} /> un tick prend plus de
              {' '}{fmtNumber(THRESHOLDS.watchAverageMs)} ms en moyenne, le rythme passe sous {fmtNumber(THRESHOLDS.watchTps, 1)} ticks par seconde, ou un pic est survenu dans la dernière heure.
            </li>
            <li>
              <StatusLight health={{ state: 'measuring', light: 'bad', title: 'Rouge', reason: '', level: null }} /> le monde est figé, le rythme passe sous
              {' '}{fmtNumber(THRESHOLDS.badTps)} ticks par seconde, un tick prend plus de {TICK_BUDGET_MS} ms en moyenne, ou les pics se répètent
              ({fmtNumber(THRESHOLDS.badSpikesPerHour)} et plus dans l'heure).
            </li>
            <li>
              <StatusLight health={{ state: 'off', light: 'none', title: 'Gris', reason: '', level: null }} /> aucune mesure à juger : profileur éteint, mesures en pause,
              serveur muet, ou serveur qui vient de démarrer (il charge son monde et n'est pas jugé pendant ses premières minutes).
              Ce n'est pas une panne, seulement une absence de verdict.
            </li>
          </ul>
        </div>
      </details>

      {failedGroups.map((g) => (
        <Note key={`err:${g.key}`} tone="bad" icon={PlugZap} title={`Mesures illisibles${g.label ? ` pour le groupe ${g.label}` : ''}`}>
          {g.error} Les serveurs de ce {g.label ? 'groupe' : 'projet'} ne sont pas listés tant que son satellite ne répond pas.
        </Note>
      ))}
      {unavailableGroups.map((g) => (
        <Note key={`old:${g.key}`} icon={Info} title={`Le profileur n'est pas encore disponible sur le satellite ${g.label ? `du groupe ${g.label}` : `du projet ${gameName}`}`}>
          Ce satellite est d'une version d'avant le profileur : il ne connaît pas encore ces mesures. Elles apparaîtront ici une fois le
          satellite mis à jour et son module « profiler » allumé. Ce n'est pas une panne.
        </Note>
      ))}
      {offGroups.map((g) => (
        <Note key={`off:${g.key}`} icon={Info} title={`Le profileur n'est pas activé sur le satellite ${g.label ? `du groupe ${g.label}` : `du projet ${gameName}`}`}>
          Le module « profiler » de ce satellite est éteint : aucune mesure n'y est reçue ni gardée. Il s'allume dans son fichier de
          configuration (<code>modules.profiler</code>), puis le mod compagnon doit être posé sur les serveurs de jeu.
        </Note>
      ))}

      {results == null ? (
        <p className={s.empty}>Lecture des mesures…</p>
      ) : (
        <>
          <div className={cx(s.tiles, loading && s.stale)}>
            <StatTile label="En difficulté" value={fmtNumber(lights.bad)} hint="jeu ralenti ou monde figé">
              <span className={cx(s.tileIcon, s.lightBad)} aria-hidden><OctagonAlert size={18} /></span>
            </StatTile>
            <StatTile label="À surveiller" value={fmtNumber(lights.watch)} hint="peu de marge, ou un pic récent">
              <span className={cx(s.tileIcon, s.lightWatch)} aria-hidden><TriangleAlert size={18} /></span>
            </StatTile>
            <StatTile label="En bonne santé" value={fmtNumber(lights.ok)} hint="le rythme est tenu">
              <span className={cx(s.tileIcon, s.lightOk)} aria-hidden><CircleCheck size={18} /></span>
            </StatTile>
            <StatTile label="Sans mesure" value={fmtNumber(lights.none)} hint="profileur éteint, serveur muet ou en démarrage">
              <span className={cx(s.tileIcon, s.lightNone)} aria-hidden><CircleDashed size={18} /></span>
            </StatTile>
          </div>

          {answered.length > 0 && measured.length === 0 && (
            <Note icon={Info} title="Aucun serveur n'a encore envoyé de mesure">
              Le satellite est prêt à en recevoir. Il faut le moteur Endium 0.1.48 ou plus récent et le mod compagnon sur les serveurs de
              jeu ; un serveur apparaît ici dès son premier message, même si son profileur est éteint.
            </Note>
          )}

          {measured.length > 0 && (
            <div className={cx(s.card, s.tableScrollX, loading && s.stale)}>
              <table className={cx(s.dataTable, s.serverTable)}>
                <thead>
                  <tr>
                    <th scope="col">État</th>
                    <th scope="col">Serveur</th>
                    <th scope="col" className={s.num} title={`Un monde fait ${TARGET_TPS} ticks par seconde quand tout va bien.`}>Ticks{NBSP}par seconde</th>
                    <th scope="col" title={`Temps moyen d'un tick sur la dernière minute, face aux ${TICK_BUDGET_MS} ms disponibles. Le repère marque le plus long tick.`}>
                      Durée d'un tick
                    </th>
                    <th scope="col" className={s.num} title="Mémoire réservée par le monde à chaque tick. Plus elle est haute, plus le nettoyage de la mémoire travaille.">Mémoire par{NBSP}tick</th>
                    <th scope="col" className={s.num} title="Temps que le monde doit rattraper pour revenir à son rythme.">Retard</th>
                    <th scope="col" className={s.num} title={`Ticks de plus de ${SPIKE_THRESHOLD_MS} ms sur la dernière heure.`}>Pics{NBSP}(1{NBSP}h)</th>
                    <th scope="col">Dernières mesures</th>
                  </tr>
                </thead>
                <tbody>
                  {measured.map((row) => (
                    <ServerRow key={row.id} row={row} now={results.find((r) => r.key === row.group)?.list?.now ?? Date.now()}
                      group={multiGroup ? groupOf(row.group) : null} onOpen={(path) => navigate(path)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {neverSeen.length > 0 && (
            <details className={s.guide}>
              <summary>
                <CircleDashed size={15} aria-hidden />
                {neverSeen.length > 1
                  ? `${fmtNumber(neverSeen.length)} serveurs de la Console n'ont encore envoyé aucune mesure`
                  : "1 serveur de la Console n'a encore envoyé aucune mesure"}
              </summary>
              <div className={s.guideBody}>
                <p>
                  Mod compagnon absent, profileur éteint au démarrage, ou moteur d'avant la 0.1.48 : on ne sait rien de leur santé.
                  Un serveur rejoint le tableau dès son premier message.
                </p>
                <ul className={s.neverList}>
                  {neverSeen.map((row) => (
                    <li key={row.id}>
                      {row.name}
                      {multiGroup && row.groupLabel ? <span className={s.muted}> · {row.groupLabel}</span> : null}
                      {row.consoleStatus === 'offline' ? <span className={s.muted}> · arrêté d'après la Console</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}

          {consoleMissing && measured.length > 0 && (
            <p className={s.footnote}>
              La liste des serveurs de la Console n'a pas pu être lue : seuls les serveurs qui ont déjà envoyé une mesure sont listés.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ServerRow({ row, now, group, onOpen }: { row: Row; now: number; group: ProfilerGroup | null; onOpen: (path: string) => void }) {
  const path = serverPath(row);
  const server = row.server;
  const measuring = row.health.state === 'measuring';
  const level = measuring && server ? headlineLevel(server) : null;
  const last = server?.lastSnapshotAt ?? server?.lastSeenAt ?? null;
  return (
    <tr className={cx(s.serverRow, path && s.serverRowLink)} onClick={path ? () => onOpen(path) : undefined}>
      <td className={s.colState}><StatusLight health={row.health} /></td>
      <th scope="row" className={s.colServer}>
        <span className={s.serverName}>
          {path ? <Link to={path} onClick={(e) => e.stopPropagation()}>{row.name}</Link> : row.name}
          {group?.label && <GroupBadge group={{ label: group.label, color: group.color ?? undefined }} />}
        </span>
        <span className={s.serverMeta}>
          {[row.instance, row.model, server && server.levels.length > 1 ? `${server.levels.length} mondes` : null].filter(Boolean).join(' · ')}
        </span>
        <span className={s.serverReason}>{row.health.level ? `Monde ${row.health.level} : ` : ''}{row.health.reason}</span>
      </th>
      {level ? (
        <>
          <td className={s.num}><strong>{fmtTps(level.ticksPerSecond)}</strong></td>
          <td className={s.colTick}>
            <span className={s.tickFigures}>
              <strong>{level.ticks > 0 ? fmtMs(level.averageTickMs) : '-'}</strong>
              <span className={s.muted}>{level.ticks > 0 ? budgetText(level.averageTickMs) : 'aucun tick fini'}</span>
            </span>
            <BudgetMeter averageMs={level.averageTickMs} maxMs={level.ticks > 0 ? level.maxTickMs : null} light={row.health.light} />
            <span className={s.muted}>plus long : {level.ticks > 0 ? fmtMs(level.maxTickMs) : '-'}</span>
          </td>
          <td className={s.num}>{fmtBytes(level.bytesPerTick)}</td>
          <td className={s.num}>{fmtMs(level.lagMs)}</td>
          <td className={s.num}>
            <strong>{fmtNumber(server!.spikesLastHour)}</strong>
            {server!.spikesLastHour > 0 && server!.maxTickMsLastHour != null && <span className={s.subValue}>jusqu'à {fmtMs(server!.maxTickMsLastHour)}</span>}
          </td>
        </>
      ) : (
        <td colSpan={5} className={s.noMeasure}>Pas de mesure à afficher</td>
      )}
      <td className={s.colLast}>{last ? fmtAgo(last, now) : 'jamais'}</td>
    </tr>
  );
}
